import { useState } from '#imports'
import { nextTick } from 'vue'

import type { Facet, UiPriceFacet } from '../types'

export function useExpandFilters() {
  const displayed = useState('plp-expand-filters-display', () => false)
  const type = useState<'filter' | 'sort'>(
    'plp-expand-filters-type',
    () => 'filter',
  )
  const selectedFacet = useState<Facet | UiPriceFacet | undefined>(
    'plp-expand-filters-selected-facet',
    () => undefined,
  )

  const triggerElement = useState<HTMLElement | null>()
  const modalElement = useState<HTMLElement | null>()

  type Position = { left?: string; right?: string }
  const position = useState<Position>('plp-expand-filters-position', () => ({}))

  function close() {
    displayed.value = false
    selectedFacet.value = undefined
    position.value = {}

    // Move the focus on the trigger element after the modal is closed
    triggerElement.value?.focus()
  }

  function filter(facet: Facet | UiPriceFacet, facetPosition: Position) {
    if (displayed.value && selectedFacet.value?.name === facet.name) close()
    else {
      type.value = 'filter'
      selectedFacet.value = facet
      position.value = facetPosition
      displayed.value = true

      // Move the focus on the modal element after it is opened
      nextTick(() => {
        modalElement.value?.focus()
      })
    }
  }

  function focusTrap(event: KeyboardEvent) {
    const { key, shiftKey, target } = event

    if (key === 'Escape') {
      close()

      return
    }

    if (modalElement.value && key === 'Tab') {
      const focusableList = modalElement.value.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )

      if (focusableList.length < 2) {
        event.preventDefault()

        return
      }

      const last = focusableList.length - 1

      if (shiftKey === false && target === focusableList[last]) {
        event.preventDefault()
        focusableList[0].focus()
      } else if (shiftKey === true && target === focusableList[0]) {
        event.preventDefault()
        focusableList[last].focus()
      }
    }
  }

  function sort(facetPosition: Position) {
    if (displayed.value && type.value === 'sort') close()
    else {
      type.value = 'sort'
      selectedFacet.value = undefined
      position.value = facetPosition
      displayed.value = true
    }
  }

  return {
    displayed,
    type,
    triggerElement,
    modalElement,
    filter,
    focusTrap,
    sort,
    close,
    selectedFacet,
    position,
  }
}
